import React from "react";
import TitleBar from "../../components/navbar/TitleBar";
import SchoolBusNavMenu from "../../components/createForm/SchoolBusNavMenu";
import Footer from "../../components/common/Footer";
import SubTitle from "../../components/common/SubTitle";
import SearchFilter from "../../components/common/SearchFilter";
import { Suspense, useState, useMemo, useEffect } from "react";
import SchoolDynamicTable from "../../components/createForm/SchoolDynamicTable";
import CreateRouteForm from "../../components/createForm/CreateRouteForm";
import axios from "axios";
import config from "../../config.js";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";

const getCoordinateKey = (latitude, longitude) =>
  `${Number(latitude).toFixed(6)}_${Number(longitude).toFixed(6)}`;

// ─── buildRouteFullData ──────────────────────────────────────────────────────
// allOrgStops: flat array of all stops from GET /stop/organization
// This is the authoritative source — no more per-coordinate API calls.
const buildRouteFullData = (route, allOrgStops = []) => {
  // Build lookup maps from ALL org stops (fast, no 500 errors)
  const stopsById = new Map(
    allOrgStops.filter((s) => s?.id).map((s) => [s.id, s]),
  );
  const stopsByCoordinate = new Map(
    allOrgStops.map((s) => [getCoordinateKey(s.latitude, s.longitude), s]),
  );

  const routeCoordinates = Array.isArray(route.routeCoordinates)
    ? [...route.routeCoordinates].sort(
        (a, b) => Number(a?.order || 0) - Number(b?.order || 0),
      )
    : [];

  if (routeCoordinates.length === 0) {
    return {
      ...route,
      stops: [],
      routePoints: [],
      origin: null,
      destination: null,
    };
  }

  // Build a route point from a single coordinate entry
  const buildPoint = (coordinate, index) => {
    const coordKey = getCoordinateKey(
      coordinate.latitude,
      coordinate.longitude,
    );

    // 1. Try matching by stopId stored on the coordinate
    const byId = coordinate.stopId ? stopsById.get(coordinate.stopId) : null;

    // 2. Try matching by coordinate against ALL org stops
    const byCoord = stopsByCoordinate.get(coordKey);

    // 3. Decide if this coordinate represents a stop
    //    Priority: explicit stopId match → coordinate match → isStop flag
    const matchedStop = byId || byCoord || null;
    const isStop = Boolean(
      matchedStop || // matched a real stop
      coordinate.isStop || // backend flagged it as stop
      coordinate.stopId, // has a stopId even if not in our list
    );

    const defaultName =
      index === 0
        ? "Origin"
        : index === routeCoordinates.length - 1
          ? "Destination"
          : isStop
            ? `Stop ${index}`
            : `IM${index}`;

    return {
      lat: Number(coordinate.latitude),
      lng: Number(coordinate.longitude),
      isStop,
      stopId: matchedStop?.id || coordinate.stopId || null,
      name: matchedStop?.name || coordinate.name || defaultName,
      id:
        matchedStop?.id ||
        coordinate.stopId ||
        coordinate.id ||
        `route-point-${route.id || route.routeId}-${index}`,
    };
  };

  const allPoints = routeCoordinates.map((coord, i) => buildPoint(coord, i));

  const originPoint = allPoints[0];
  const destinationPoint = allPoints[allPoints.length - 1];
  const midPoints = allPoints.length > 2 ? allPoints.slice(1, -1) : [];

  // Ordered stops: origin + any intermediate stops + destination
  const orderedStops = [
    originPoint,
    ...midPoints.filter((p) => p.isStop),
    destinationPoint?.id !== originPoint?.id ? destinationPoint : null,
  ]
    .filter(Boolean)
    .map((p) => ({
      id: p.stopId,
      latitude: p.lat,
      longitude: p.lng,
      name: p.name,
    }));

  return {
    ...route,
    stops: orderedStops,
    routePoints: midPoints, // only intermediate points (no origin/dest)
    origin: {
      id: originPoint.stopId,
      latitude: originPoint.lat,
      longitude: originPoint.lng,
      name: originPoint.name,
    },
    destination: {
      id: destinationPoint.stopId,
      latitude: destinationPoint.lat,
      longitude: destinationPoint.lng,
      name: destinationPoint.name,
    },
  };
};

export default function SchoolBusRoutes() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [routesData, setRoutesData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [vehicleOptions, setVehicleOptions] = useState([]);

  const headers = [
    { key: "Route ID", value: "IDs" },
    { key: "Route Name", value: "Names" },
    { key: "Pick-up", value: "Pick-up" },
    { key: "Drop", value: "Drop" },
    { key: "Vehicles", value: "Vehicles" },
    { key: "actions", value: "" },
  ];

  const filters = useMemo(
    () => [
      {
        id: "vehicles",
        label: "Vehicles",
        helperText: "Select vehicle",
        options: vehicleOptions,
      },
    ],
    [vehicleOptions],
  );

  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId;

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    setIsLoading(true);
    try {
      // ── Fetch routes and ALL org stops in parallel (2 calls, not N×M) ──
      const [routesResponse, stopsResponse] = await Promise.all([
        axios.get(`${config.baseURL}/route/organization`, {
          params: { organizationId: orgId },
          withCredentials: true,
        }),
        axios.get(`${config.baseURL}/stop/organization`, {
          withCredentials: true,
        }),
      ]);

      if (!Array.isArray(routesResponse.data)) {
        setRoutesData([]);
        setIsLoading(false);
        return;
      }

      // All stops flat list — used for coordinate matching inside buildRouteFullData
      const allOrgStops = Array.isArray(stopsResponse.data)
        ? stopsResponse.data
        : [];

      // Build full route data using the org stops list (no per-coordinate API calls)
      const sortedRoutes = [...routesResponse.data].sort((a, b) => {
        const aTime = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const bTime = new Date(b.createdAt || b.updatedAt || 0).getTime();

        if (aTime !== bTime) {
          return bTime - aTime;
        }

        return Number(b.id || 0) - Number(a.id || 0);
      });

      const routesWithStops = sortedRoutes.map((route) =>
        buildRouteFullData(route, allOrgStops),
      );

      // Extract unique vehicles for filter dropdown
      const uniqueVehicles = [];
      const vehicleIds = new Set();
      routesWithStops.forEach((route) => {
        (route.vehicles || []).forEach((vehicle) => {
          if (vehicle.id && !vehicleIds.has(vehicle.id)) {
            vehicleIds.add(vehicle.id);
            uniqueVehicles.push({
              value: vehicle.id.toString(),
              label: vehicle.name || vehicle.VehicleNumber,
            });
          }
        });
      });
      setVehicleOptions(uniqueVehicles);

      // Transform for table display
      const transformedData = routesWithStops.map((route) => {
        const pickUp = route.origin?.name || route.name || "N/A";
        const drop = route.destination?.name || pickUp;
        const vehicleNames =
          route.vehicles?.length > 0
            ? route.vehicles.map((v) => v.name || v.VehicleNumber).join(", ")
            : "N/A";

        return {
          "Route ID": route.routeId || "N/A",
          "Route Name": route.name || "N/A",
          "Pick-up": pickUp,
          Drop: drop,
          Vehicles: vehicleNames,
          fullData: route,
        };
      });

      console.log("Transformed Routes Data:", transformedData);
      setRoutesData(transformedData);
    } catch (error) {
      console.error("Error fetching routes:", error);
      if (error.response?.status === 401) navigate("/");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (value) => setSearchQuery(value);
  const handleCreateRoute = () => {
    setShowCreateForm(true);
    setExpandedRow(null);
  };
  const handleFilterChange = (values) => setFilterValues(values);
  const handleCloseForm = () => setShowCreateForm(false);

  const handleSubmitRoute = async (formData, vehicleIds) => {
    try {
      const response = await axios.post(
        `${config.baseURL}/route`,
        { ...formData, organizationId: orgId },
        { withCredentials: true },
      );
      if (response.status === 200 || response.status === 201) {
        const newRouteId = response.data.id;
        await axios.patch(
          `${config.baseURL}/route/vehicles`,
          {
            routeId: newRouteId,
            vehicleIds: Array.isArray(vehicleIds) ? vehicleIds : [],
          },
          { withCredentials: true },
        );
        await fetchRoutes();
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error("Error creating route:", error);
    }
  };

  const handleUpdateRoute = async (formData, vehicleIds) => {
    if (expandedRow === null) return;
    try {
      const selectedRoute = filteredData[expandedRow]?.fullData;
      if (!selectedRoute?.id) return;
      const routeId = selectedRoute.id;

      await axios.put(
        `${config.baseURL}/route`,
        {
          id: routeId,
          routeId: formData.routeId,
          name: formData.name,
          routeColor: formData.routeColor,
          organizationId: orgId,
        },
        { withCredentials: true },
      );
      await axios.patch(
        `${config.baseURL}/route/coordinates`,
        {
          id: routeId,
          routeCoordinates: formData.routeCoordinates,
          routeType: formData.routeType,
          routeColor: formData.routeColor,
        },
        { withCredentials: true },
      );
      await axios.patch(
        `${config.baseURL}/route/vehicles`,
        { routeId, vehicleIds: Array.isArray(vehicleIds) ? vehicleIds : [] },
        { withCredentials: true },
      );
      await fetchRoutes();
      setExpandedRow(null);
    } catch (error) {
      console.error("Error updating route:", error);
    }
  };

  const handleDeleteRoute = async () => {
    if (expandedRow === null) return;
    const routeToDelete = filteredData[expandedRow];
    if (!routeToDelete?.fullData?.id) return;
    try {
      const response = await axios.delete(`${config.baseURL}/route`, {
        data: { id: Number(routeToDelete.fullData.id), organizationId: orgId },
        withCredentials: true,
      });
      if (response.status === 200) {
        await fetchRoutes();
        setExpandedRow(null);
      }
    } catch (error) {
      console.error("Error deleting route:", error);
      throw error; // ← re-throw so CreateRouteForm can display it
    }
  };

  const handleRowClick = (rowIndex) => {
    setExpandedRow(expandedRow === rowIndex ? null : rowIndex);
    setShowCreateForm(false);
  };

  const handleCloseExpandedForm = () => setExpandedRow(null);

  const filteredData = useMemo(() => {
    let filtered = [...routesData];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (route) =>
          route["Route ID"].toLowerCase().includes(query) ||
          route["Route Name"].toLowerCase().includes(query) ||
          route["Pick-up"].toLowerCase().includes(query) ||
          route["Drop"].toLowerCase().includes(query) ||
          route["Vehicles"].toLowerCase().includes(query),
      );
    }
    if (filterValues.vehicles) {
      filtered = filtered.filter((route) =>
        (route.fullData.vehicles || []).some(
          (v) => String(v.id) === String(filterValues.vehicles),
        ),
      );
    }
    return filtered;
  }, [searchQuery, filterValues, routesData]);

  const renderExpandedRow = (row) => (
    <CreateRouteForm
      onClose={handleCloseExpandedForm}
      onSubmit={handleUpdateRoute}
      onDelete={handleDeleteRoute}
      initialData={row.fullData}
      isEdit={true}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "100vh",
      }}
    >
      <Box>
        <TitleBar />
        <SchoolBusNavMenu />
        <SubTitle
          title="Routes"
          add="+ Create Routes"
          onAddClick={handleCreateRoute}
        />
        <div className="table_container">
          {showCreateForm && (
            <CreateRouteForm
              onClose={handleCloseForm}
              onSubmit={handleSubmitRoute}
              isEdit={false}
            />
          )}
          <SearchFilter
            searchPlaceholder="search"
            filters={filters}
            onSearchChange={handleSearchChange}
            onFilterChange={handleFilterChange}
            dataDownload={true}
            downloadRows={filteredData}
            downloadColumns={headers
              .filter((header) => header.key !== "actions")
              .map((header) => ({
                key: header.key,
                label: header.value,
              }))}
            downloadFileName="school-routes"
          />
          <Suspense fallback={<div className="loader"></div>}>
            <SchoolDynamicTable
              headers={headers}
              data={filteredData}
              loading={isLoading}
              expandedRow={expandedRow}
              onRowClick={handleRowClick}
              renderExpandedRow={renderExpandedRow}
            />
          </Suspense>
        </div>
      </Box>
      <Footer />
    </Box>
  );
}
